import { defineStore } from "pinia";
import type { WebModelGetCountry } from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import { getAllCountries } from "~/model/api/pLCnextStoreFrontendAPI";
import { withErrorHandling } from "~/helpers/withErrorHandling";
import { withErrorMessage } from "~/helpers/withErrorMessage";
import { withLoading } from "~/helpers/withLoading";
import { getCountryName } from "~/helpers/countryNames";
import { sortBy } from "~/helpers/sortBy";

interface State {
  countries: WebModelGetCountry[];
}

export interface CountryWithName extends WebModelGetCountry {
  name: string;
}

export const useCountriesStore = defineStore("countries", {
  state: (): State => ({
    countries: [], // initial state
  }),
  getters: {
    getCountriesWithName: (state): CountryWithName[] => {
      return (state.countries ?? [])
        .filter(
          (country): country is WebModelGetCountry & { iso: string } =>
            typeof country.iso === "string" && country.iso.length > 0,
        )
        .map((country) => ({
          ...country,
          name: getCountryName(country.iso), // iso is now string, not undefined
        }))
        .sort(
          sortBy({ key: "name", order: "asc" }, { key: "iso", order: "asc" }),
        );
    },
    getCountryOptionsForSelectField(): { value: string; text: string }[] {
      return this.getCountriesWithName
        .filter((country) => country.iso) // remove undefined
        .map((country) => ({
          value: country.iso!, // safe after filter
          text: country.name,
        }));
    },
    getCountryWithNameByIso() {
      return (iso: string | undefined): CountryWithName | undefined => {
        if (!iso) return undefined;
        return this.getCountriesWithName.find((country) => country.iso === iso);
      };
    },
  },
  actions: {
    async loadCountries() {
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(getAllCountries())),
      );
      console.debug("loadCountries: ", data, error);
      if (data) {
        this.countries = data;
      }
    },

    clear() {
      this.$reset();
    },
  },
});
